import {
    ButtonGradientPrimary,
    ButtonSecondary,
    MultilineInput,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

import { ProfileFormFields, profileSchema, type ProfileFormData } from '../../validation';

import { FormField } from '@/components/FormField/FormField';
import { useAmelioTranslations } from '@/hooks';
import { CharacterCount } from '@/pages/Competencies/components/profiles/CharacterCount';
import { validationMessageKey } from '@/utils/validationMessage';

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

type ProfileFormProps = {
    mode: 'create' | 'edit';
    defaultValues: ProfileFormData;
    isSubmitting: boolean;
    onSubmit: (data: ProfileFormData) => void;
    onCancel: () => void;
};

export const ProfileForm = ({
    mode,
    defaultValues,
    isSubmitting,
    onSubmit,
    onCancel,
}: ProfileFormProps) => {
    const { t } = useAmelioTranslations();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema),
        defaultValues,
    });

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className={'flex flex-col gap-4 p-2'}
        >
            <div className={'mb-4'}>
                <h4 className={'text-navy text-[20px] font-medium'}>
                    {mode === 'edit'
                        ? t('competencies.profiles.form.editTitle')
                        : t('competencies.profiles.form.createTitle')}
                </h4>

                <p className={'text-md font-normal text-gray-400'}>
                    {mode === 'edit' ? t('competencies.profiles.editDescription') : ''}
                </p>
            </div>

            <Controller
                control={control}
                name={ProfileFormFields.name}
                render={({ field }) => (
                    <FormField
                        label={t('competencies.profiles.form.namePlaceholder')}
                        required={true}
                        errorMessage={
                            errors.name?.message
                                ? t(validationMessageKey(errors.name.message))
                                : undefined
                        }
                    >
                        <TextInput
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            placeholder={t('competencies.profiles.form.namePlaceholder')}
                            dataTestId={'profile-name-input'}
                            maxLength={NAME_MAX_LENGTH}
                            variant={'white'}
                            className={'border border-gray-200 bg-white text-[14px]'}
                        />
                        <div className={'mt-2'}>
                            <CharacterCount
                                value={field.value}
                                maxLength={NAME_MAX_LENGTH}
                            />
                        </div>
                    </FormField>
                )}
            />

            <Controller
                control={control}
                name={ProfileFormFields.description}
                render={({ field }) => (
                    <FormField label={t('competencies.profiles.form.descriptionLabel')}>
                        <MultilineInput
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            placeholder={t('competencies.profiles.form.descriptionPlaceholder')}
                            rows={3}
                            autoGrow={true}
                            dataTestId={'profile-description-input'}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            variant={'white'}
                            className={
                                'focus-within:ring-primary/20 border border-gray-200 bg-white text-[14px] focus-within:ring-2'
                            }
                        />
                        <div className={'mt-2'}>
                            <CharacterCount
                                value={field.value}
                                maxLength={DESCRIPTION_MAX_LENGTH}
                            />
                        </div>
                    </FormField>
                )}
            />

            <div className={'mt-2 flex items-center justify-end gap-3'}>
                <ButtonSecondary
                    type={'button'}
                    size={'md'}
                    onClick={onCancel}
                    dataTestId={'profile-cancel'}
                    className={'!w-[80px] !min-w-[80px]'}
                >
                    {t('common.cancel')}
                </ButtonSecondary>

                <ButtonGradientPrimary
                    type={'submit'}
                    size={'md'}
                    disabled={isSubmitting}
                    dataTestId={'profile-submit'}
                    className={'!w-[80px] !min-w-[80px]'}
                >
                    {t('common.save')}
                </ButtonGradientPrimary>
            </div>
        </form>
    );
};
